import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface GuestEditPublicSettings {
  edit_enabled: boolean;
  disabled_message: string | null;
  guest_table_selection_enabled?: boolean;
}

export interface GuestEditSettings extends GuestEditPublicSettings {
  id?: number;
  user_id?: number;
  updated_at?: string;
}

/**
 * Client for the per-tenant guest edit page switch
 * (Back_End_Server /api/v1/guest-edit-settings).
 *
 * Management calls are JWT-authed — the AuthInterceptor attaches the token
 * for every environment.baseUrl request. getPublicSettings is the unauthed
 * endpoint the guest edit-reservation page uses; a tenant with no saved row
 * comes back as edit-enabled.
 */
@Injectable({ providedIn: 'root' })
export class GuestEditSettingsService {
  private readonly apiUrl = `${environment.baseUrl}/api/v1/guest-edit-settings`;

  constructor(private http: HttpClient) {}

  /**
   * Guest edit flag for a tenant — public, no token. When lang is given
   * (browser language, e.g. 'de'), the backend translates the disabled
   * message server-side, falling back to the original wording.
   */
  getPublicSettings(
    userId: number | string,
    lang?: string
  ): Observable<GuestEditPublicSettings> {
    return this.http
      .get<{ message: string; data: GuestEditPublicSettings }>(
        `${this.apiUrl}/public/${userId}`,
        { params: lang ? { lang } : {} }
      )
      .pipe(map((res) => res.data));
  }

  /** The logged-in tenant's settings; defaults when never saved. */
  getSettings(): Observable<GuestEditSettings> {
    return this.http
      .get<{ message: string; data: GuestEditSettings }>(this.apiUrl)
      .pipe(map((res) => res.data));
  }

  updateSettings(settings: {
    edit_enabled: boolean;
    disabled_message?: string | null;
    guest_table_selection_enabled?: boolean;
  }): Observable<GuestEditSettings> {
    return this.http
      .put<{ message: string; data: GuestEditSettings }>(this.apiUrl, settings)
      .pipe(map((res) => res.data));
  }
}
